import debounce from 'debounce-promise';
import { when } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX, ReactNode } from 'react';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FormatOptionLabelMeta } from 'react-select-modern';

import type {
  CustomField,
  ICustomField,
  IDefaultField,
  IiMISContactMapping,
  ImisIntegration,
  TIntegrationPersonAttributes,
} from '@feathr/blackbox';
import { defaultPartnerAttributes, FieldCollection } from '@feathr/blackbox';
import type { IUniversalSelectSingleProps } from '@feathr/components';
import { UniversalSelect } from '@feathr/components';
import ImisPersonAttributes from '@feathr/extender/App/Settings/Account/ImisIntegrationPage/ImisSyncWizard/ImisPersonAttributes';
import DataRequestBreadcrumbAttributes from '@feathr/extender/components/CustomFieldSelect/DataRequestBreadcrumbAttributes';
import {
  formatCollectionFieldOptionLabel,
  formatFieldOptionLabel,
} from '@feathr/extender/components/SelectOptions';
import { useStore } from '@feathr/extender/state';
import { DEFAULT_DEBOUNCE_WAIT } from '@feathr/hooks';
import type { ListResponse } from '@feathr/rachis';

const attributesMap = {
  [FieldCollection.Partner]: defaultPartnerAttributes,
  [FieldCollection.Person]: ImisPersonAttributes,
  [FieldCollection.Breadcrumb]: DataRequestBreadcrumbAttributes,
};

interface IProps
  extends Pick<
    IUniversalSelectSingleProps<ICustomField>,
    | 'disabled'
    | 'isClearable'
    | 'isLoading'
    | 'menuPortalTarget'
    | 'onClear'
    | 'onSelectSingle'
    | 'placeholder'
  > {
  contexts: FieldCollection[];
  integration: ImisIntegration;
  object: IiMISContactMapping;
}

function ImisCustomFieldSelect({
  contexts,
  integration,
  isLoading,
  object,
  ...additionalProps
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const { CustomFields } = useStore();
  const defaultFields: IDefaultField[] = contexts.reduce((acc, context) => {
    return acc.concat(attributesMap[context]);
  }, []);
  const [mappings, setMappings] = useState<IiMISContactMapping[] | undefined>(undefined);

  useEffect(() => {
    async function getMappings(): Promise<void> {
      // TODO: Refactor to use ContactMappings.list() as part of #2651.
      const mappings = await integration.getContactMappings();
      setMappings(mappings);
    }

    getMappings();
  }, [integration]);

  const fetchedField = object.custom_field ? CustomFields.get(object.custom_field) : undefined;
  const isFieldPending = fetchedField?.isPending ?? false;
  const fieldCollection = fetchedField?.get('collection');
  const fieldDataType = fetchedField?.get('data_type');
  const fieldLabel = fetchedField?.get('label');
  const fieldUKey = fetchedField?.get('u_key');

  // `.toJS()` exposes `data_type` to the renderer; memoized so react-select's identity check holds.
  const fetchedFieldValue: ICustomField | undefined = useMemo(
    () => (fetchedField && !isFieldPending ? fetchedField.toJS() : undefined),
    [fetchedField, fieldCollection, fieldDataType, fieldLabel, fieldUKey, isFieldPending],
  );

  const value: IDefaultField | ICustomField | undefined = object.custom_field
    ? fetchedFieldValue
    : defaultFields.find(({ id }) => id === object.default_field);

  const currentlyMappedFields: Array<string | null> =
    mappings?.map(
      (mapping: IiMISContactMapping) => mapping.default_field ?? mapping.custom_field,
    ) ?? [];

  // Keyed on the mapped set because `defaultOptions={true}` loads its list only once, on mount.
  const mappedFieldsKey = mappings ? currentlyMappedFields.join('|') : 'pending-mappings';

  // TODO: Remove the Address field from the list of options since it a pseudo-field that should only be available for the iMIS Address Field
  const loadOptions = async (inputValue: string): Promise<Array<IDefaultField | ICustomField>> => {
    const customFields: ListResponse<CustomField> = CustomFields.list({
      filters: {
        collection__in: contexts,
        is_archived__ne: true,
        u_key__icontains: inputValue,
      },
      pagination: {
        page_size: 20,
      },
    });
    await when(() => !customFields.isPending);
    return [
      ...defaultFields.filter((attribute: IDefaultField) =>
        attribute.u_key.toLowerCase().includes(inputValue.toLowerCase()),
      ),
      ...customFields.models.map((customField: CustomField) => customField.toJS()),
    ]
      .filter(
        // Remove fields that are already mapped except for the currently mapped field
        (field: ICustomField | IDefaultField) =>
          !currentlyMappedFields?.includes(field.id as TIntegrationPersonAttributes) ||
          field.id === object.default_field,
      )
      .filter(
        // Keep only fields with the same data type as the object
        (field: ICustomField | IDefaultField) => {
          // If the data_type is a number type, allow both to display
          if (field.data_type === 'int' || field.data_type === 'float') {
            return object.data_type === 'int' || object.data_type === 'float';
          }
          return field.data_type === object.data_type;
        },
      );
  };

  const debouncedLoadOptions = debounce(loadOptions, DEFAULT_DEBOUNCE_WAIT);

  // Renders a blank label rather than throwing in case u_key is absent.
  function getOptionLabel(option: ICustomField): string {
    return option.u_key ?? '';
  }

  function getOptionValue({ id }: ICustomField): string {
    return id as string;
  }

  function formatOptionLabel(
    field: ICustomField,
    meta: FormatOptionLabelMeta<ICustomField>,
  ): ReactNode {
    return contexts.length > 1
      ? formatCollectionFieldOptionLabel(field, meta, t)
      : formatFieldOptionLabel(field, meta);
  }

  return (
    <UniversalSelect<ICustomField>
      {...additionalProps}
      async={true}
      defaultOptions={true}
      formatOptionLabel={formatOptionLabel}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
      isLoading={isLoading || isFieldPending}
      key={mappedFieldsKey}
      loadOptions={debouncedLoadOptions}
      name={'imis-custom-field'}
      value={value as ICustomField | undefined}
    />
  );
}

export default observer(ImisCustomFieldSelect);
